import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { CheckCircle, Loader2, XCircle, Plane, Mail, Phone, ShieldCheck, RefreshCw, CreditCard, PiggyBank, CalendarClock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_POLLS = 12;
const fmtDate = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '');
const money = (n, cur = 'GBP') => `${cur === 'GBP' ? '£' : cur + ' '}${Number(n || 0).toFixed(2)}`;

const DepositPanel = ({ fp, currency }) => (
  <CardContent className="p-6 bg-orange-50 border-t border-orange-100" data-testid="deposit-balance-panel">
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div className="text-sm text-orange-900">
        <div className="flex items-center gap-2 font-bold text-base"><PiggyBank className="h-5 w-5" /> Deposit of {money(fp.deposit, currency)} received — seats held</div>
        <div className="mt-1 flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Balance <strong data-testid="deposit-balance-due">{money(fp.balance_due, currency)}</strong> due by <strong data-testid="deposit-balance-due-date">{fmtDate(fp.balance_due_date)}</strong></div>
        <div className="mt-1 text-xs text-orange-800">Deposit refundable subject to a £{Number(fp.admin_fee || 10).toFixed(0)} administration fee. Tickets are issued once the balance is paid. The payment link has also been emailed to you.</div>
      </div>
      {fp.balance_url && (
        <Button asChild className="h-12 px-6 bg-[#FF6600] hover:bg-[#e65c00] text-white font-bold" data-testid="pay-balance-link-btn">
          <a href={fp.balance_url}><CreditCard className="h-5 w-5 mr-2" />Pay balance online</a>
        </Button>
      )}
    </div>
  </CardContent>
);

export const FlightPaymentSuccess = ({ bookingRef, onGoHome }) => {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [polls, setPolls] = useState(0);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (!bookingRef) { setError('No booking reference found'); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/payments/status?booking_id=${encodeURIComponent(bookingRef)}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Unable to verify payment');
        if (cancelled) return;
        setStatus(data);
        const issuing = data.paid && data.type === 'FLIGHT_PREPAY' && !data.pnr && data.pnr_status !== 'FAILED';
        if ((!data.paid && !data.failed && polls < MAX_POLLS) || (issuing && polls < MAX_POLLS * 2)) setTimeout(() => setPolls((p) => p + 1), 4000);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [bookingRef, polls]);

  const fp = status?.flight;
  const isDeposit = fp?.payment_plan === 'DEPOSIT';
  const isBalance = status?.type === 'FLIGHT_BALANCE';

  const retryPayment = async () => {
    if (isBalance && fp?.balance_url) { window.location.href = fp.balance_url; return; }
    setRetrying(true);
    try {
      const res = await fetch(`${API_URL}/api/flights/payment/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pnr: bookingRef, origin_url: window.location.origin })
      });
      const data = await res.json();
      if (!res.ok || !data.checkout_url) throw new Error(data.detail || 'Unable to restart payment');
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.message);
      setRetrying(false);
    }
  };

  const pnr = status?.pnr;
  const paid = status?.paid || status?.pnr_status === 'ISSUED';
  const failed = status?.failed && !(pnr && !isBalance);
  const issuing = paid && status?.type === 'FLIGHT_PREPAY' && !pnr && status?.pnr_status !== 'FAILED';
  const pnrFailed = paid && status?.pnr_status === 'FAILED';
  const pending = status && !paid && !failed;
  const timedOut = pending && polls >= MAX_POLLS;
  const flight = status?.booking?.flight_data || {};
  const currency = status?.currency || 'GBP';

  const headerClass = paid ? (isDeposit && !isBalance ? 'bg-[#FF6600]' : 'bg-emerald-600') : failed ? 'bg-red-600' : error ? 'bg-slate-700' : 'bg-brand-600';
  const title = pnrFailed ? (isDeposit ? 'Deposit Received — We\'re Finalising Your Reservation' : 'Payment Received — We\'re Finalising Your Ticket')
    : issuing ? (isDeposit ? 'Deposit Received — Holding Your Seats…' : 'Payment Successful — Issuing Your Ticket…')
    : paid ? (isBalance ? 'Balance Received — Booking Fully Paid' : isDeposit ? 'Deposit Received — Seats Held' : 'Payment Successful — Booking Confirmed')
    : failed ? 'Payment Didn\'t Go Through'
    : error ? 'Payment Verification Failed'
    : timedOut ? 'Payment Still Processing' : 'Confirming Your Payment…';
  const subtitle = pnrFailed ? 'Your payment is safe. The airline system didn\'t return a reference automatically, so our team is confirming your seats by hand and will email your reference shortly.'
    : issuing ? (isDeposit ? 'Your deposit has been taken and we\'re creating your airline reservation now. This takes a few seconds.' : 'Your card has been charged and we\'re creating your airline booking reference now. This takes a few seconds.')
    : paid ? (isBalance ? 'Thank you — nothing more to pay. Our team will issue your tickets and email your e-ticket shortly.'
      : isDeposit ? 'Your seats are reserved under the reference below. Your reservation confirmation and a secure link to pay the balance have been emailed to you.'
      : 'Your booking is confirmed. Your e-ticket confirmation and receipt have been emailed to you.')
    : failed ? `Your card was ${status.state === 'cancelled' ? 'not charged — the payment was cancelled' : 'declined'}${status.failure_reason ? ` (${status.failure_reason})` : ''}. ${isBalance ? 'Your seats remain held — you can try again with the same or a different card.' : 'Your seats are still reserved — you can try again with the same or a different card.'}`
    : error ? error
    : timedOut ? 'We\'ll email you as soon as the bank confirms. Your reference is safe.'
    : 'This usually takes a few seconds. Please don\'t close this page.';

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10" data-testid="flight-payment-success-page">
      <div className="max-w-2xl mx-auto space-y-6">
        <Card className="overflow-hidden border-0 shadow-2xl">
          <div className={`py-10 text-center text-white ${headerClass}`}>
            {!status && !error && <Loader2 className="h-14 w-14 mx-auto mb-4 animate-spin" />}
            {paid && <CheckCircle className="h-14 w-14 mx-auto mb-4" />}
            {(failed || error) && <XCircle className="h-14 w-14 mx-auto mb-4" />}
            {pending && !timedOut && <Loader2 className="h-14 w-14 mx-auto mb-4 animate-spin" />}
            {timedOut && <RefreshCw className="h-14 w-14 mx-auto mb-4" />}
            <h1 className="text-2xl sm:text-3xl font-bold" data-testid="payment-result-title">{title}</h1>
            <p className="mt-2 text-white/85 text-sm sm:text-base max-w-lg mx-auto" data-testid="payment-result-subtitle">{subtitle}</p>
          </div>

          <div className="bg-slate-900 p-8 text-center">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">{pnr ? 'Airline booking reference (PNR)' : issuing ? 'Issuing PNR…' : 'Booking reference'}</div>
            <div className="text-5xl sm:text-6xl font-black tracking-[0.2em] text-white select-all" data-testid="payment-success-pnr">
              {pnr || (issuing ? <Loader2 className="h-12 w-12 mx-auto animate-spin text-slate-400" /> : bookingRef)}
            </div>
            {pnr && pnr !== bookingRef && <div className="text-xs text-slate-500 mt-2">Payment ref {bookingRef}</div>}
            {status && (
              <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                <Badge className={`${paid ? (isDeposit && !isBalance ? 'bg-[#FF6600]' : 'bg-emerald-500') : failed ? 'bg-red-500' : 'bg-amber-500'} text-white px-3 py-1 text-sm`} data-testid="payment-status-badge">
                  {paid ? (isBalance ? 'PAID IN FULL' : isDeposit ? 'DEPOSIT PAID' : 'PAID') : (status.state || 'pending').toUpperCase()}
                </Badge>
                <span className="text-slate-300 text-sm" data-testid="payment-amount">{money(status.amount, currency)}{isDeposit && fp?.total ? <span className="text-slate-500"> of {money(fp.total, currency)}</span> : ''}</span>
              </div>
            )}
          </div>

          {paid && isDeposit && !isBalance && fp && fp.balance_due > 0 && <DepositPanel fp={fp} currency={currency} />}

          {(failed || timedOut) && status?.can_retry && (
            <CardContent className="p-6 bg-red-50 border-t border-red-100">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-red-800">
                  <div className="font-bold">Nothing has been charged.</div>
                  <div>Try again now, or call us and we'll take payment over the phone.</div>
                </div>
                <Button onClick={retryPayment} disabled={retrying} className="h-12 px-6 bg-brand-600 hover:bg-brand-700 text-white font-bold" data-testid="retry-payment-btn">
                  {retrying ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <CreditCard className="h-5 w-5 mr-2" />}
                  {retrying ? 'Redirecting…' : 'Try payment again'}
                </Button>
              </div>
            </CardContent>
          )}

          {status?.booking && (
            <CardContent className="p-6 grid gap-4 sm:grid-cols-2">
              <div className="flex items-start gap-3 rounded-xl bg-slate-50 p-4">
                <Plane className="h-5 w-5 text-brand-600 mt-0.5" />
                <div>
                  <div className="text-xs text-slate-500 uppercase">Route</div>
                  <div className="font-bold text-slate-900">{flight.from || flight.origin} → {flight.to || flight.destination}</div>
                  <div className="text-sm text-slate-600">{flight.departure_date}{flight.return_departure_date ? ` · back ${flight.return_departure_date}` : ''}</div>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-xl bg-slate-50 p-4">
                <Mail className="h-5 w-5 text-brand-600 mt-0.5" />
                <div>
                  <div className="text-xs text-slate-500 uppercase">{paid ? 'Confirmation sent to' : 'Receipt will be sent to'}</div>
                  <div className="font-bold text-slate-900 break-all">{status.contact_email || status.booking?.contact?.email}</div>
                  <div className="text-sm text-slate-600">{(status.booking.passengers || []).length} passenger(s)</div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-600">
          <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Secured by Revolut · ATOL protected</div>
          <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-brand-600" /> 01908 220000 · 01582 808050</div>
        </div>

        <Button onClick={onGoHome} variant={failed ? 'outline' : 'default'} className={`w-full h-12 font-semibold ${failed ? '' : 'bg-brand-600 hover:bg-brand-700 text-white'}`} data-testid="payment-success-home-btn">
          Back to Flight380
        </Button>
      </div>
    </div>
  );
};
